/**
 * Pricing Table — starter content and the shape of a price.
 *
 * A plan is a fixed sequence of part blocks. The plan locks its template, so
 * this list is not a starting point an author can edit away from — it is the
 * plan's structure, and PLAN_TEMPLATE is the single place it is written down.
 */
import { __ } from '@wordpress/i18n';

/**
 * How many options a table will carry.
 *
 * Not a technical limit — the mechanism scales as far as the CSS loop does.
 * It is the point past which a chooser stops helping anyone decide, and it is
 * mirrored by the same ceiling in every renderer that reads the list.
 */
export const MAX_PRICING_OPTIONS = 6;

/**
 * One option's worth of a plan's numbers.
 *
 * Lives on the price block rather than the plan: it is that part's own data,
 * and keeping it there is what makes selecting the price give you the price's
 * controls and nothing else.
 *
 * @return {Object} An empty price row.
 */
export const emptyPrice = () => ({ amount: '', period: '', note: '' });

/**
 * A new, unnamed option on the table's list.
 *
 * @return {Object} An empty option.
 */
export const emptyOption = () => ({ label: '', badge: '' });

/**
 * The button's "every option" value.
 *
 * A real index scopes the button to one option; this says the button belongs
 * to all of them, and is why a plan needs only one. Negative so it can never
 * collide with an index, and mirrored by the same test in render.php.
 */
export const ALL_OPTIONS = -1;

/**
 * Pad a price list to the table's option count without dropping what is there.
 *
 * Called whenever the parent's option list changes: adding an option must not
 * wipe the price already written, and removing one must not leave a row the
 * editor no longer shows a field for.
 *
 * @param {Array}  prices    Current prices.
 * @param {number} termCount How many options the table now has.
 * @return {Array} Prices, exactly termCount long.
 */
export function alignPrices(prices, termCount) {
  const current = Array.isArray(prices) ? prices : [];
  return Array.from(
    { length: termCount },
    (_, index) => current[index] || emptyPrice()
  );
}

/**
 * The parts of a plan, in order.
 *
 * One button, shown under every option. There used to be one per option, which
 * only ever made sense while there were exactly two of them — at six, changing
 * a word on the button means changing it six times. A plan that genuinely needs
 * a different checkout per option can still scope its buttons in their own
 * settings, and add one per option.
 *
 * The button is a real `brandy/button`, so a pricing CTA is the same block as
 * every other button on the site.
 *
 * @param {Object} plan Copy for this plan.
 * @return {Array} An inner-block template.
 */
const button = (label) => [
  'brandy/buttons',
  {},
  [['brandy/button', { text: label, url: '#' }]],
];

const planParts = (plan) => [
  ['brandy/pricing-text', { role: 'badge', content: plan.badge ?? '' }],
  ['brandy/pricing-text', { role: 'name', content: plan.name }],
  ['brandy/pricing-text', { role: 'tagline', content: plan.tagline }],
  ['brandy/pricing-price', { prices: plan.prices }],
  [
    'brandy/pricing-body',
    {},
    [
      [
        'brandy/icon-list',
        {},
        plan.features.map((text) => ['brandy/icon-list-item', { text }]),
      ],
    ],
  ],
  ['brandy/pricing-cta', { term: ALL_OPTIONS }, [button(plan.cta)]],
];

/**
 * The structure of a plan, with placeholders. Used when a plan is inserted on
 * its own rather than as part of the starter table.
 */
export const PLAN_TEMPLATE = planParts({
  badge: '',
  name: '',
  tagline: '',
  prices: [emptyPrice(), emptyPrice()],
  features: [
    __('First feature', 'brandy-blocks'),
    __('Second feature', 'brandy-blocks'),
    __('Third feature', 'brandy-blocks'),
  ],
  cta: '',
});

/*
 * THE STARTER TABLE
 *
 * Everything below is the design an author gets on insert, and it is a finished
 * one rather than a set of placeholders: three cards, the middle one recommended
 * by a full-width ribbon across its top edge.
 *
 * Literal colours, not palette presets. The starter is a drawing, and its
 * emphasis depends on three shades of one hue agreeing with each other — a
 * theme that retunes `brandy-primary` alone would leave the soft card fill and
 * the ribbon out of step. An author who wants their own palette changes these
 * in the sidebar like any other block's colours.
 */
const ACCENT = '#374ddd';
const ACCENT_SOFT = '#eff1fc';
const INK = '#1c1c1c';
const MUTED = '#6a6a6a';
const CARD_BORDER = '#dbdbdc';
const BUTTON_BORDER = '#898989';
const WHITE = '#ffffff';

const CARD_PAD = '30px';

/*
 * A plain card pads down by the height of the featured card's ribbon plus that
 * card's own top padding, so all three plan names sit on one line. Stated as a
 * number because nothing in CSS can measure a sibling's ribbon.
 */
const CARD_PAD_TOP = '68px';

const GUTTER = { left: CARD_PAD, right: CARD_PAD };

/**
 * The recommended card runs its ribbon edge to edge, which means the card can
 * carry no padding of its own — so every other part of it pays for its own
 * gutter. A plain card is padded normally and its parts ask for nothing.
 *
 * @param {boolean} featured Whether this is the recommended plan.
 * @param {Object}  extra    Further spacing for this part.
 * @return {Object} A `style` fragment, or nothing at all.
 */
const partSpacing = (featured, extra = {}) => {
  const spacing = {
    ...extra,
    ...(featured ? { padding: { ...GUTTER, ...extra.padding } } : {}),
  };

  // An empty object still serializes, so a plain card's parts get no `spacing`
  // key at all rather than an inert one.
  return Object.keys(spacing).length ? { spacing } : {};
};

/**
 * The card itself.
 *
 * @param {boolean} featured Whether this is the recommended plan.
 * @return {Object} The plan's `style` attribute.
 */
const cardStyle = (featured) => ({
  spacing: {
    padding: featured
      ? { top: '0px', right: '0px', bottom: '0px', left: '0px' }
      : {
          top: CARD_PAD_TOP,
          right: CARD_PAD,
          bottom: CARD_PAD,
          left: CARD_PAD,
        },
    blockGap: '14px',
  },
  border: {
    radius: '8px',
    width: featured ? '2px' : '1px',
    style: 'solid',
    color: featured ? ACCENT : CARD_BORDER,
  },
  color: { background: featured ? ACCENT_SOFT : WHITE },
});

/**
 * The ribbon across the top of the recommended card.
 *
 * A Group rather than the plan's own badge part: the badge is a pill placed by
 * `align-self`, and no combination of its controls reaches the card's edges.
 * A Group can, because the card gives up its padding to let it.
 *
 * @param {string} label Ribbon copy.
 * @return {Array} An inner-block template.
 */
const ribbon = (label) => [
  'core/group',
  {
    metadata: { name: __('Ribbon', 'brandy-blocks') },
    style: {
      spacing: {
        padding: { top: '10px', bottom: '10px', left: '20px', right: '20px' },
      },
      /*
       * The card's 8px radius less its own 2px border. A square corner here
       * would show through the card's rounded one.
       */
      border: {
        radius: {
          topLeft: '6px',
          topRight: '6px',
          bottomLeft: '0px',
          bottomRight: '0px',
        },
      },
      color: { background: ACCENT },
    },
    layout: { type: 'constrained' },
  },
  [
    [
      'core/paragraph',
      {
        content: label,
        fontSize: 'extra-small',
        style: {
          /*
           * Centred through typography rather than the paragraph's `align`,
           * which no longer moves text on its own.
           */
          typography: {
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            textAlign: 'center',
          },
          color: { text: WHITE },
          elements: { link: { color: { text: WHITE } } },
          spacing: { margin: { top: '0px', bottom: '0px' } },
        },
      },
    ],
  ],
];

/**
 * The ticked feature list.
 *
 * `regular` rather than a heavier weight on purpose: IconCssService only emits
 * glyph rules for the regular, fill and duotone faces, so any other type name
 * renders an empty box with no error anywhere.
 *
 * @param {Array} features Lines of copy.
 * @return {Array} An inner-block template.
 */
const featureList = (features) => [
  'brandy/icon-list',
  {
    iconSize: 18,
    iconColor: ACCENT,
    iconGap: 12,
    gap: 14,
    style: { color: { text: INK } },
    fontSize: 'small',
  },
  features.map((text) => [
    'brandy/icon-list-item',
    { iconName: 'check', iconType: 'regular', text },
  ]),
];

/**
 * The call to action: filled on the recommended card, outlined on the others.
 *
 * @param {string}  label    Button copy.
 * @param {boolean} featured Whether this is the recommended plan.
 * @return {Array} An inner-block template.
 */
const starterButton = (label, featured) => [
  'brandy/buttons',
  {},
  [
    [
      'brandy/button',
      {
        text: label,
        width: 100,
        fontSize: 'base',
        style: {
          border: {
            radius: '6px',
            color: featured ? ACCENT : BUTTON_BORDER,
            width: '1px',
            style: 'solid',
          },
          spacing: { padding: { top: '14px', bottom: '14px' } },
          color: {
            background: featured ? ACCENT : WHITE,
            text: featured ? WHITE : INK,
          },
          elements: { link: { color: { text: featured ? WHITE : INK } } },
          typography: { fontWeight: '600' },
        },
      },
    ],
  ],
];

/**
 * One designed card.
 *
 * The part sequence deliberately differs from `planParts` above: the price
 * leads the tagline here, which is the order this design reads in, and the
 * badge is a ribbon rather than a pill. Classic layout positions by DOM order
 * alone, so authoring the parts in this order is all that takes.
 *
 * @param {Object} plan Copy, numbers and emphasis for this plan.
 * @return {Array} An inner-block template.
 */
const starterPlanParts = (plan) => {
  const featured = !!plan.badge;

  return [
    ...(featured ? [ribbon(plan.badge)] : []),
    [
      'brandy/pricing-text',
      {
        role: 'name',
        content: plan.name,
        fontSize: '2-xl',
        style: {
          typography: { fontWeight: '700' },
          color: { text: INK },
          // Clears the ribbon by the same distance the plain cards pad by.
          ...partSpacing(featured, featured ? { margin: { top: '16px' } } : {}),
        },
      },
    ],
    [
      'brandy/pricing-price',
      {
        prices: plan.prices,
        fontSize: 'base',
        style: { color: { text: INK }, ...partSpacing(featured) },
      },
    ],
    [
      'brandy/pricing-text',
      {
        role: 'tagline',
        content: plan.tagline,
        fontSize: 'small',
        style: { color: { text: MUTED }, ...partSpacing(featured) },
      },
    ],
    [
      'brandy/pricing-body',
      { style: partSpacing(featured, { margin: { top: '16px' } }) },
      [featureList(plan.features)],
    ],
    [
      'brandy/pricing-cta',
      {
        /*
         * Margin, not padding — the CTA supports only the one. It is also what
         * closes the recommended card, which has no bottom padding to do it.
         */
        style: {
          spacing: {
            margin: featured
              ? { top: '16px', bottom: CARD_PAD, ...GUTTER }
              : { top: '16px' },
          },
        },
      },
      [starterButton(plan.cta, featured)],
    ],
  ];
};

const PLANS = [
  {
    name: 'Free',
    tagline: 'Everything to get a single site faster.',
    badge: '',
    prices: [
      { amount: '$0', period: 'forever', note: '' },
      { amount: '$0', period: '/forever', note: '' },
    ],
    /*
     * HTML, not plain text — the list item stores its copy as rich text, and
     * whatever is written here is what lands in the post. A bare ampersand
     * round-trips, but it saves an unescaped one; the entity is what an author
     * typing the same line into the editor would produce.
     */
    features: [
      '200 image optimizations / month',
      'Auto-optimize on upload',
      'WebP conversion &amp; smart resize',
      'Lossy, Glossy &amp; Lossless levels',
      'Original image backups',
      'Optimization dashboard &amp; stats',
    ],
    cta: 'Sign up free',
  },
  {
    name: 'Pro',
    tagline: 'For busy sites that optimize at scale.',
    badge: 'Most popular',
    prices: [
      { amount: '$5', period: '/ month', note: '' },
      { amount: '$48', period: '/year', note: '' },
    ],
    features: [
      'Everything in Free',
      '5,000 optimizations / month',
      'Priority processing queue',
      'Bulk optimization presets',
      'Priority email support',
    ],
    cta: 'Get started',
  },
  {
    name: 'Agency',
    tagline: 'Manage optimization across every client.',
    badge: '',
    prices: [
      { amount: '$15', period: '/ month', note: '' },
      { amount: '$144', period: '/year', note: '' },
    ],
    features: [
      'Everything in Pro',
      '25,000 optimizations / month',
      'Up to 25 connected sites',
      'FileBird media organization',
      'White-glove onboarding',
    ],
    cta: 'Get started',
  },
];

/**
 * A chooser, then three plans in a group — the shape most pricing pages land
 * on, and enough content that there is something to switch between on insert.
 *
 * The chooser leads, because that is where it belongs for a table of plans: it
 * governs all of them at once. Dragging it into a single card is what turns the
 * block into a one-plan offer, and nothing about the markup has to change for
 * that to work. Its colours are inverted against the accent — a white pill on
 * the page, indigo type inside it — so it reads as part of the same set as the
 * cards without competing with the ribbon.
 *
 * The plans are wrapped rather than laid out by the table because a wrapper is
 * what makes the layout the author's. The table still arranges plans that are
 * its own direct children — content saved before this, and anyone who deletes
 * the group — but it no longer has to be the only way to arrange them.
 */
export const PRICING_TABLE_TEMPLATE = [
  [
    'brandy/pricing-options',
    { accentColor: WHITE, activeTextColor: ACCENT, fontSize: 'small' },
  ],
  [
    'core/group',
    {
      metadata: { name: __('Plans', 'brandy-blocks') },
      align: 'wide',
      /*
       * A fixed three columns rather than a minimum width: the ribbon marks one
       * card out of three, and a row that reflows to two leaves it off-centre.
       * Both the legacy responsive attribute and the native viewport keys are
       * written, because the legacy engine is always on and the native one is
       * what newer editors read — a card row that only carried one of them
       * would stay three-up on a phone for half the sites running it.
       */
      layout: { type: 'grid', columnCount: 3, minimumColumnWidth: null },
      responsiveLayout: {
        enabled: true,
        tablet: { columnType: 'manual', value: '1' },
        mobile: { columnType: 'manual', value: '1' },
      },
      style: {
        spacing: { blockGap: '20px' },
        '@tablet': { layout: { columnCount: 1 } },
        '@mobile': { layout: { columnCount: 1 } },
      },
    },
    PLANS.map((plan) => [
      'brandy/pricing-plan',
      { style: cardStyle(!!plan.badge) },
      starterPlanParts(plan),
    ]),
  ],
];
